import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { useIntl } from "react-intl";
import { Chat42Error } from "@/core/error";
import { VIEW_PERMISSION_MAP } from "@/util/permissionUtil";
import { useAgent } from "@/query/agent";
import { usePermissions } from "@/query/permission";
import { useAgentId, useRouteHandler } from "@/hook";
import { Error404View } from "@/component/error/ErrorView";
import AgentContextSideMenu from "@/component/agentContext/AgentContextSideMenu";
import AgentContentLayout from "@/component/agentContext/AgentContentLayout";

import DashboardView from "./dashboard/DashboardView";
import IntentView from "./intent/IntentView";
import SelectorView from "./selector/SelectorView";
import EntityView from "./entity/EntityView";
import SynonymView from "./synonym/SynonymView";
import TypoView from "./typo/TypoView";
import AutocompleteView from "./autocomplete/AutocompleteView";
import BulkTestView from "./test/BulkTestView";
import DeploymentView from "./deployment/DeploymentView";
import MemberView from "./member/MemberView";
import SettingView from "./setting/SettingView";
import EntityValueView from "./entityValue/EntityValueView";
import AutocompleteKeywordView from "./autocompleteKeyword/AutocompleteKeywordView";
import BulkTestReportView from "./test/BulkTestReportView";


const AgentContextView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const location = useLocation();
    const { isPending } = useAgent(agentId);
    const { removeStateKey } = useRouteHandler();

    const [resetKey, setResetKey] = useState<number>(0);

    const { data: permissions, isFetched } = usePermissions(agentId);

    useEffect(() => {
        if (isFetched && permissions && permissions.length === 0) {
            throw new Chat42Error("403");
        }
    }, [permissions, isFetched]);

    useEffect(() => {
        if (location.state?.from === "sidemenu") {
            setResetKey(prev => prev + 1);
            removeStateKey("from");
        }
    }, [location, setResetKey, removeStateKey]);

    if (isPending) {
        return null;
    }
    return (
        <div className="w-screen h-screen flex bg-black-100">
            <AgentContextSideMenu />
            <Routes key={resetKey}>
                <Route index element={<Navigate replace to="dashboard" />}/>
                <Route path="dashboard" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m02" })}
                        permissions={VIEW_PERMISSION_MAP.DASHBOARD}
                    >
                        <DashboardView />
                    </AgentContentLayout>
                }/>
                <Route path="intent" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m03" })}
                        permissions={VIEW_PERMISSION_MAP.INTENT}
                    >
                        <IntentView />
                    </AgentContentLayout>
                }/>
                <Route path="selector" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m04" })}
                        permissions={VIEW_PERMISSION_MAP.SELECTOR}
                    >
                        <SelectorView />
                    </AgentContentLayout>
                }/>
                <Route path="entity" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m05" })}
                        permissions={VIEW_PERMISSION_MAP.ENTITY}
                    >
                        <EntityView />
                    </AgentContentLayout>
                }/>
                <Route path="entity/:entityId" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m05" })}
                        permissions={VIEW_PERMISSION_MAP.ENTITY}
                    >
                        <EntityValueView />
                    </AgentContentLayout>
                }/>
                <Route path="synonym" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m06" })}
                        permissions={VIEW_PERMISSION_MAP.SYNONYM}
                    >
                        <SynonymView />
                    </AgentContentLayout>
                }/>
                <Route path="typo" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m07" })}
                        permissions={VIEW_PERMISSION_MAP.TYPOS}
                    >
                        <TypoView />
                    </AgentContentLayout>
                }/>
                <Route path="autocomplete" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m08" })}
                        permissions={VIEW_PERMISSION_MAP.AUTOCOMPLETE}
                    >
                        <AutocompleteView />
                    </AgentContentLayout>
                }/>
                <Route path="autocomplete/:autocompleteId" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m08" })}
                        permissions={VIEW_PERMISSION_MAP.AUTOCOMPLETE}
                    >
                        <AutocompleteKeywordView />
                    </AgentContentLayout>
                }/>
                <Route path="test" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m09" })}
                        permissions={VIEW_PERMISSION_MAP.BULK_TEST}
                    >
                        <BulkTestView />
                    </AgentContentLayout>
                }/>
                <Route path="test/:testId" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m09" })}
                        permissions={VIEW_PERMISSION_MAP.BULK_TEST_RESULT}
                    >
                        <BulkTestReportView />
                    </AgentContentLayout>
                }/>
                <Route path="deploy" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m10" })}
                        permissions={VIEW_PERMISSION_MAP.DEPLOYMENT}
                    >
                        <DeploymentView />
                    </AgentContentLayout>
                }/>
                <Route path="member" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m12" })}
                        permissions={VIEW_PERMISSION_MAP.MEMBER}
                    >
                        <MemberView />
                    </AgentContentLayout>
                }/>
                <Route path="setting" element={
                    <AgentContentLayout
                        label={intl.formatMessage({ id: "m13" })}
                        permissions={VIEW_PERMISSION_MAP.SETTING}
                    >
                        <SettingView />
                    </AgentContentLayout>
                }/>
                <Route path="*" element={<Error404View />}/>
            </Routes>
        </div>
    );
};

export default AgentContextView;